import { AppwriteException, ID, OAuthProvider } from 'appwrite';
import { appwriteConfig } from '../../config/environment';
import type { SessionProfile, UserBond } from '../../types/session';
import { runCommand } from './command-gateway';
import { reportClientError } from './report-client-error';
import { account, functions } from './client';

const USER_CACHE_KEY = 'rs-auth-user';
const PROFILE_CACHE_KEY = 'rs-auth-profile';
const ROLES_CACHE_KEY = 'rs-auth-roles';

export interface RegisterInput {
  username: string;
  name: string;
  lastName: string;
  email: string;
  password: string;
}

/** A quién reenviar el código, sin volver a pedir usuario/email. */
export interface PendingCode {
  userId: string;
  email: string;
}

export interface SessionGateway {
  load(): Promise<SessionProfile | null>;
  signInWithGoogle(): Promise<void>;
  /** `identifier` es el usuario o el email; se resuelve solo si hace falta. */
  signInWithPassword(identifier: string, password: string): Promise<void>;
  /** Crea la cuenta y manda el primer código de verificación. */
  register(input: RegisterInput): Promise<PendingCode>;
  /** Reenvía el código, de registro o de recuperación: es el mismo mecanismo. */
  resendCode(pending: PendingCode): Promise<void>;
  /** Valida el código y abre sesión. Sirve tanto para verificar el registro
   * como para el primer paso de recuperar contraseña. */
  verifyCode(pending: PendingCode, code: string): Promise<void>;
  requestPasswordReset(identifier: string): Promise<PendingCode>;
  /** Requiere haber pasado por `verifyCode` primero: dejó la sesión abierta
   * que esta llamada necesita para autorizarse. */
  confirmPasswordReset(newPassword: string): Promise<void>;
  signOut(): Promise<void>;
}

function unauthorized(error: unknown): boolean {
  if (error instanceof AppwriteException) return error.code === 401 || error.code === 403;
  if (typeof error !== 'object' || error === null || !('code' in error)) return false;
  const code = Number((error as { code?: unknown }).code);
  return code === 401 || code === 403;
}

function networkFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  return error instanceof Error && /failed to fetch|network|load failed|fetch failed/i.test(error.message);
}

async function withNetworkRetry<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (!networkFailure(error)) throw error;
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    return operation();
  }
}

function clearSharedCache(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
    localStorage.removeItem(PROFILE_CACHE_KEY);
    localStorage.removeItem(ROLES_CACHE_KEY);
  } catch {
    // La sesión real sigue siendo Appwrite. La copia local solo mejora la transición entre aplicaciones.
  }
}

function saveSharedCache(profile: SessionProfile): void {
  try {
    localStorage.setItem(
      USER_CACHE_KEY,
      JSON.stringify({
        $id: profile.user.id,
        name: profile.user.name,
        email: profile.user.email,
      }),
    );
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ datos: profile, en: Date.now() }));
    localStorage.setItem(ROLES_CACHE_KEY, JSON.stringify(profile.roles));
  } catch {
    // No se bloquea el panel si el navegador impide usar almacenamiento local.
  }
}

function cachedProfileFor(userId: string): SessionProfile | null {
  try {
    const raw = JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) ?? 'null') as { datos?: unknown; en?: unknown } | null;
    if (!raw || typeof raw.en !== 'number' || Date.now() - raw.en > 24 * 60 * 60_000) return null;
    const profile = raw.datos as SessionProfile | undefined;
    return profile?.user?.id === userId ? profile : null;
  } catch {
    return null;
  }
}

async function profileWithFallback(userId: string, cached: SessionProfile | null): Promise<SessionProfile> {
  const request = withNetworkRetry(() =>
    functions.createExecution({
      functionId: appwriteConfig.accountFunctionId,
      body: JSON.stringify({ action: 'get_session_profile' }),
      async: false,
    }),
  ).then((execution) => parseProfile(execution.responseBody));

  if (!cached) return request;

  return Promise.race([
    request,
    new Promise<SessionProfile>((resolve) => window.setTimeout(() => resolve(cached), 2_000)),
  ]);
}

const validBonds = new Set<UserBond>([
  'admin',
  'staff',
  'organizer',
  'participant',
  'newcomer',
  'member',
]);

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function numberValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function parseProfile(responseBody: string): SessionProfile {
  const parsed: unknown = JSON.parse(responseBody || '{}');
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('El servidor devolvió un perfil con un formato no reconocido.');
  }

  const source = parsed as Record<string, unknown>;
  if (typeof source.error === 'string' && source.error) throw new Error(source.error);

  const rawUser = (source.user ?? {}) as Record<string, unknown>;
  const rawProfile = (source.profile ?? {}) as Record<string, unknown>;
  const rawActivities = (source.activities ?? {}) as Record<string, unknown>;
  const rawBond = stringValue(source.bond);
  const userId = stringValue(rawUser.id);

  if (!userId || !validBonds.has(rawBond as UserBond)) {
    throw new Error('El servidor devolvió un perfil incompleto.');
  }

  return {
    user: {
      id: userId,
      name: stringValue(rawUser.name),
      email: stringValue(rawUser.email),
    },
    profile: {
      exists: rawProfile.exists === true,
      displayName: stringValue(rawProfile.displayName),
      username: stringValue(rawProfile.username),
      notificationSoundsEnabled: rawProfile.notificationSoundsEnabled !== false,
      notificationEmailsEnabled: rawProfile.notificationEmailsEnabled !== false,
      notificationToastsEnabled: rawProfile.notificationToastsEnabled !== false,
    },
    roles: Array.isArray(source.roles)
      ? source.roles.filter((role): role is string => typeof role === 'string')
      : [],
    isStaff: source.isStaff === true,
    isAdmin: source.isAdmin === true,
    permissions: Array.isArray(source.permissions)
      ? source.permissions.filter((permission): permission is string => typeof permission === 'string')
      : [],
    activities: {
      organizes: numberValue(rawActivities.organizes),
      participates: numberValue(rawActivities.participates),
      pending: numberValue(rawActivities.pending),
    },
    bond: rawBond as UserBond,
  };
}

/** No repara nada: solo deja ver qué se está perdiendo cuando esta comprobación falla (ver report-client-error.ts). */
function errorDetails(error: unknown): Record<string, unknown> {
  if (error instanceof AppwriteException) {
    return { code: error.code, type: error.type, response: error.response };
  }
  return { name: error instanceof Error ? error.name : typeof error };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function load(): Promise<SessionProfile | null> {
  let userId = '';
  try {
    const user = await withNetworkRetry(() => account.get());
    userId = user.$id;
  } catch (error) {
    if (unauthorized(error)) {
      clearSharedCache();
      return null;
    }
    reportClientError({ source: 'web-session', operation: 'account.get', message: errorMessage(error), details: errorDetails(error) });
    throw error;
  }

  try {
    // `get_session_profile` vive en `account`, no en `management-command`:
    // llamarlo con `managementFunctionId` respondía "unknown_action" y
    // dejaba la sesión sin poder comprobarse — confirmado en
    // `system_errors` (ver report-client-error.ts) antes de corregir esto.
    const profile = await profileWithFallback(userId, cachedProfileFor(userId));
    saveSharedCache(profile);
    return profile;
  } catch (error) {
    reportClientError({
      source: 'web-session',
      operation: 'get_session_profile',
      message: errorMessage(error),
      details: { functionId: appwriteConfig.accountFunctionId, ...errorDetails(error) },
    });
    throw error;
  }
}

async function signInWithGoogle(): Promise<void> {
  // Si ya se intentaba entrar a algo concreto (una mesa de chat, un perfil…),
  // el login vuelve ahí. Solo desde la portada misma (`/`) se manda al panel
  // personal, porque no tiene sentido volver a la portada pública después de
  // identificarse.
  const atHome = window.location.pathname === '/';
  const destination = atHome ? '/panel' : window.location.pathname;
  const success = `${window.location.origin}${destination}`;
  const failure = `${window.location.origin}${window.location.pathname}?auth=failed`;
  await account.createOAuth2Session({
    provider: OAuthProvider.Google,
    success,
    failure,
  });
}

/** El usuario o email escrito ya viaja a `resolve_account`; devuelve el email real. */
async function resolveIdentifier(identifier: string): Promise<PendingCode> {
  return runCommand<PendingCode>('resolve_account', { identifier: identifier.trim() });
}

async function signInWithPassword(identifier: string, password: string): Promise<void> {
  const trimmed = identifier.trim();
  const email = trimmed.includes('@') ? trimmed : (await resolveIdentifier(trimmed)).email;
  await account.createEmailPasswordSession({ email, password });
}

async function register(input: RegisterInput): Promise<PendingCode> {
  const userId = ID.unique();
  const email = input.email.trim();
  await account.create({
    userId,
    email,
    password: input.password,
    name: `${input.name.trim()} ${input.lastName.trim()}`.trim(),
  });
  await account.createEmailToken({ userId, email });
  return { userId, email };
}

async function resendCode(pending: PendingCode): Promise<void> {
  await account.createEmailToken({ userId: pending.userId, email: pending.email });
}

async function verifyCode(pending: PendingCode, code: string): Promise<void> {
  await account.createSession({ userId: pending.userId, secret: code.trim() });
}

async function requestPasswordReset(identifier: string): Promise<PendingCode> {
  const pending = await resolveIdentifier(identifier);
  await account.createEmailToken({ userId: pending.userId, email: pending.email });
  return pending;
}

async function confirmPasswordReset(newPassword: string): Promise<void> {
  await runCommand('reset_password', { password: newPassword });
}

async function signOut(): Promise<void> {
  try {
    await account.deleteSession({ sessionId: 'current' });
  } finally {
    clearSharedCache();
  }
}

export const appwriteSessionGateway: SessionGateway = {
  load,
  signInWithGoogle,
  signInWithPassword,
  register,
  resendCode,
  verifyCode,
  requestPasswordReset,
  confirmPasswordReset,
  signOut,
};
